import { type PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import type {
  RegisterDialog,
  RegisterDialogRequest,
  RegisterDialogResponse,
} from '../../types/helsenorge.helseregistre.internalapi.entities.entities';
import type { RootState } from '../store';

import { get } from '@helsenorge/framework-utils/hn-proxy-service';

export const initialRegisterDialogState: RegisterDialogTypesState = {
  RegisterDialoger: {},
};
export interface RegisterDialogTypesState {
  RegisterDialoger: {
    [key: string]: {
      loading: boolean;
      loaded: boolean;
      error: boolean;
      registerDialog?: RegisterDialog | null;
    };
  };
}

export const generateUniqueKey = (args: RegisterDialogRequest): string =>
  JSON.stringify({ registerId: args.registerId, herId: args.herId });

export const keyCanExist = (args: RegisterDialogRequest, state: Partial<RegisterDialogTypesState>): boolean => {
  const hasRegisterId = !!args.registerId;
  const uniqueKeyFromRegisterIdAndHerId = generateUniqueKey(args);
  return (hasRegisterId || !!args.herId) && !!state.RegisterDialoger && !(uniqueKeyFromRegisterIdAndHerId in state.RegisterDialoger);
};

export const fetchRegisterDialogTyper = createAsyncThunk(
  'registerdialog/GET',
  async ({ registerId: RegisterId, herId: HerId }: RegisterDialogRequest) => {
    return await get<RegisterDialogResponse>('helseregistre', 'api/v1/RegisterDialog', {
      RegisterId: RegisterId ?? '',
      HerId: HerId,
    });
  },
  {
    condition: (args: RegisterDialogRequest, { getState }) => {
      const state = (getState() as RootState).registerdialogtypes;
      const key = generateUniqueKey(args);

      return (
        !!key &&
        keyCanExist(args, state) &&
        (!state.RegisterDialoger[key] || (!state.RegisterDialoger[key].loaded && !state.RegisterDialoger[key].error))
      );
    },
  }
);
const registerDialogSlice = createSlice({
  name: 'registerdialog',
  initialState: initialRegisterDialogState,
  reducers: {
    REMOVE_KEY(state: RegisterDialogTypesState, action: PayloadAction<RegisterDialogRequest>) {
      const key = generateUniqueKey(action.payload);
      if (key) {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { [key]: _, ...newRegisterDialoger } = state.RegisterDialoger;
        state.RegisterDialoger = newRegisterDialoger;
      }
    },
  },
  extraReducers: builder => {
    builder.addCase(fetchRegisterDialogTyper.pending, (state: RegisterDialogTypesState, action) => {
      const uniqueKeyFromRegisterIdAndHerId = generateUniqueKey(action.meta.arg);
      if (keyCanExist(action.meta.arg, state)) {
        state.RegisterDialoger[uniqueKeyFromRegisterIdAndHerId] = {
          loading: true,
          loaded: false,
          error: false,
          registerDialog: null,
        };
      }
    });
    builder.addCase(fetchRegisterDialogTyper.rejected, (state: RegisterDialogTypesState, action) => {
      const uniqueKeyFromRegisterIdAndHerId = generateUniqueKey(action.meta.arg);

      if (uniqueKeyFromRegisterIdAndHerId) {
        state.RegisterDialoger[uniqueKeyFromRegisterIdAndHerId] = {
          loading: false,
          loaded: false,
          error: true,
          registerDialog: null,
        };
      }
    });
    builder.addCase(fetchRegisterDialogTyper.fulfilled, (state: RegisterDialogTypesState, action) => {
      if (action.payload) {
        const uniqueKeyFromRegisterIdAndHerId = generateUniqueKey(action.meta.arg);
        if (uniqueKeyFromRegisterIdAndHerId) {
          state.RegisterDialoger[uniqueKeyFromRegisterIdAndHerId] = {
            loading: false,
            loaded: true,
            error: false,
            registerDialog: action.payload.registerDialog,
          };
        }
      }
    });
  },
});

export const { REMOVE_KEY } = registerDialogSlice.actions;

export default registerDialogSlice.reducer;
